import { NextPage } from 'next'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { Seo } from '@/layout/Seo'
import { useMetaForProject } from '@/layout/Seo/ProjectSeoHelper'
import { getAllProjectLivestreamsSorted, Livestream } from '@/services/LivestreamService'
import { getProjectMeta, ProjectMeta } from '@/services/ProjectsService'
import { logger } from '@/utils/logging'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { loadTranslations } from '@/utils/translate/translate-server'
import { WatchProjectLivestreamsView, WatchProjectLivestreamsViewProps } from '@/views/WatchProjectLivestreamsView'

interface WatchProjectLivestreamsPageProps extends Omit<WatchProjectLivestreamsViewProps, 'livestreams'> {
  projectMeta: ProjectMeta
  locale: string
  livestreams: Livestream[]
}

export const WatchProjectLivestreamsPage: NextPage<WatchProjectLivestreamsPageProps> = ({
  projectMeta,
  livestreams,
  locale,
}) => {
  const meta = useMetaForProject({ ...projectMeta, locale: locale })

  const title = `Watch ${projectMeta.name} Livestreams | Live Shows, Events & Premieres`
  const description = `Tune in to all ${projectMeta.name} livestreams — from exclusive premieres and cast interviews to live events and giveaways.`

  return (
    <ProjectMetaProvider {...projectMeta}>
      <Seo {...meta} title={title} description={description} />
      <WatchProjectLivestreamsView livestreams={livestreams} projectMeta={projectMeta} />
    </ProjectMetaProvider>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler<WatchProjectLivestreamsPageProps>(
  async ({ federationClient, locale, resolvedUrl, params, next404 }) => {
    try {
      const translations = await loadTranslations(locale, ['common', 'watch'])
      const projectSlug = params?.project as string

      if (!projectSlug) {
        logger().error('Project slug is required', { resolvedUrl })
        return next404()
      }

      const projectMeta = await getProjectMeta({ slug: projectSlug }, federationClient, { locale })

      if (!projectMeta) {
        logger().warn('Project not found', { projectSlug, resolvedUrl })
        return next404()
      }

      const allLivestreams = await getAllProjectLivestreamsSorted({ locale })

      const projectLivestreams =
        allLivestreams
          .filter((livestream) => livestream.projectSlug === projectSlug)
          .map((livestream) => ({
            ...livestream,
            unavailableReason: livestream.unavailableReason ?? null,
          })) ?? []

      return {
        props: {
          projectMeta,
          livestreams: projectLivestreams,
          locale,
          ...translations,
        },
      }
    } catch (error) {
      logger().error(`Error fetching livestreams page data for ${resolvedUrl}`, { error })
      throw error
    }
  },
)

export default WatchProjectLivestreamsPage
